<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { useQuickSearchSelectionStore } from '@/stores/quickSearchSelectionStore';

/**
 * The "5 fields" chip inside a search box (SAU-467). It says how many fields the search
 * looks in, counted across every record type it covers, and opens and closes the tag
 * rows under the box.
 */
const props = defineProps<{
    entityIds: string[];
    /** The id of the panel the chip opens, for aria-controls. */
    panelId: string;
    /** True when a popover trigger around the chip opens and closes it, so the chip's own click does not. */
    triggeredByParent?: boolean;
}>();

const open = defineModel<boolean>('open', { required: true });

const selectionStore = useQuickSearchSelectionStore();
const fieldCount = computed(() =>
    props.entityIds.reduce((total, entityId) => total + selectionStore.selectionFor(entityId).fields.length, 0),
);
</script>

<template>
  <button
    type="button"
    :aria-expanded="open"
    :aria-controls="panelId"
    data-testid="quick-search-fields-chip"
    class="inline-flex h-6 shrink-0 items-center gap-1 rounded-full bg-foreground/[0.06] px-2.5 text-xs text-secondary-foreground hover:text-foreground aria-expanded:bg-primary/10 aria-expanded:text-foreground"
    @click.prevent="triggeredByParent || (open = !open)"
  >
    {{ fieldCount }} {{ fieldCount === 1 ? 'field' : 'fields' }}
    <ChevronDownIcon class="size-3" :class="open && 'rotate-180'" aria-hidden="true" />
  </button>
</template>
